/**
 * Enterprise Lesson 5: Produce a Show
 * Capstone synthesis — combines workspace setup, live camera, a matchup graphic,
 * scoreboards, ScoreBird live data, and the Video Board finale.
 * L1-style stepped format. Audio URLs TODO.
 */
import type { EnterpriseLessonDef } from '@graphicsim/shared-types';

// TODO: audio narration files (Lesson_E5-*.mp3) to be recorded.
// Leave audioUrl undefined for now — Next button enables immediately without audio.
export const entGfx05: EnterpriseLessonDef = {
  id: 'ent-gfx-05',
  name: 'Produce a Show',
  mode: 'steps',
  sections: [
    // Section 1: Set Up the Show
    {
      id: 'sec-1',
      title: 'Set Up the Show',
      steps: [
        {
          id: '1a',
          type: 'info',
          title: 'Your First Broadcast',
          instruction: 'In this lesson you will produce a complete football broadcast end to end, using everything from Lessons 1 through 4 — workspace setup, live camera, a matchup graphic, a scoreboard with live data, and the Video Board.',
          // audioUrl: 'TODO: Lesson_E5-01.mp3',
        },
        {
          id: '1b',
          type: 'action',
          title: 'Switch to the Live Workspace',
          instruction: 'Click the "Live" toggle at the top-right of the toolbar to switch into the Live workspace layout.',
          highlightSelector: '#icon-toolbar',
          completionTrigger: {
            eventName: 'ui:workspaceChanged',
            matchField: 'mode',
            matchValue: 'live',
          },
          allowedSelectors: ['#icon-toolbar', '.toggle-btn'],
          // audioUrl: 'TODO: Lesson_E5-02.mp3',
        },
      ],
    },
    // Section 2: Pre-Game Camera Feed
    {
      id: 'sec-2',
      title: 'Pre-Game Camera Feed',
      steps: [
        {
          id: '2a',
          type: 'action',
          title: 'Add Your Live Camera',
          instruction: 'Add a Blackmagic Decklink camera feed: File > New > Live Audio/Video > Blackmagic Decklink. The camera will appear in your Project panel and start sending video to the Program Monitor immediately.',
          highlightSelector: '#menubar',
          completionTrigger: {
            eventName: 'project:itemAdded',
            matchField: 'item',
            matchValue: 'contains:Decklink',
          },
          allowedSelectors: ['#menubar', '.menu-dropdown', '.menu-submenu'],
          // audioUrl: 'TODO: Lesson_E5-03.mp3',
        },
      ],
    },
    // Section 3: Introduce the Matchup
    {
      id: 'sec-3',
      title: 'Introduce the Matchup',
      steps: [
        {
          id: '3a',
          type: 'info',
          title: 'Open with a Matchup Graphic',
          instruction: 'Broadcasts usually open with a matchup graphic introducing the two teams. Let\'s drop one onto your live camera feed.',
          // audioUrl: 'TODO: Lesson_E5-04.mp3',
        },
        {
          id: '3b',
          type: 'action',
          title: 'Add the Matchup Graphic',
          instruction: 'In the Graphics Library, open the Football folder and drag "Football Matchup" into the Project panel.',
          highlightSelector: '#panel-library',
          completionTrigger: {
            eventName: 'project:itemAdded',
            matchField: 'item',
            matchValue: 'contains:Matchup',
          },
          allowedSelectors: ['#panel-library', '#panel-project'],
          // audioUrl: 'TODO: Lesson_E5-05.mp3',
        },
        {
          id: '3c',
          type: 'action',
          title: 'Set the Matchup Fields',
          instruction: 'Select the Matchup in your Project panel, then use the Properties panel to fill in the game details: change Home Team, Away Team, and at least two more fields — the records, the matchup label, or the detail line. Click Next when the graphic reads the way you want it.',
          highlightSelector: '#panel-properties',
          completionTrigger: {
            eventName: 'properties:fieldChanged',
            matchField: 'itemName',
            matchValue: 'contains:Matchup',
            distinctField: 'fieldId',
            count: 4,
          },
          requireNextClick: true,
          allowedSelectors: ['#panel-project', '#panel-properties'],
          // audioUrl: 'TODO: Lesson_E5-06.mp3',
        },
        {
          id: '3d',
          type: 'action',
          title: 'Match the Team Colors',
          instruction: 'Still in Properties, set Home Color and Away Color to match the two teams. The color stripe and underline on each side of the matchup update live in the Preview.',
          highlightSelector: '#panel-properties',
          completionTrigger: {
            eventName: 'properties:fieldChanged',
            matchField: 'fieldId',
            matchValue: 'oneOf:homeColor|awayColor',
            distinctField: 'fieldId',
            count: 2,
          },
          requireNextClick: true,
          allowedSelectors: ['#panel-project', '#panel-properties'],
          // audioUrl: 'TODO: Lesson_E5-07.mp3',
        },
        {
          id: '3e',
          type: 'action',
          title: 'Take the Matchup Live',
          instruction: 'Click the Play button on the Matchup row in your Project panel to fade it onto the Program Monitor over your live camera.',
          highlightSelector: '#panel-project',
          completionTrigger: {
            eventName: 'monitor:takeToProgram',
            matchField: 'itemName',
            matchValue: 'contains:Matchup',
          },
          allowedSelectors: ['#panel-project', '.project-play-btn'],
          // audioUrl: 'TODO: Lesson_E5-08.mp3',
        },
        {
          id: '3f',
          type: 'action',
          title: 'Take the Matchup Off Air',
          instruction: 'Click the Play button on the Matchup row again to fade it back off. Clearing it now keeps it from overlapping the scoreboard you are about to bring in.',
          highlightSelector: '#panel-project',
          completionTrigger: {
            eventName: 'monitor:removedFromProgram',
            matchField: 'itemName',
            matchValue: 'contains:Matchup',
          },
          allowedSelectors: ['#panel-project', '.project-play-btn'],
          // audioUrl: 'TODO: Lesson_E5-09.mp3',
        },
      ],
    },
    // Section 4: Bring in the Scoreboard with Live Data
    {
      id: 'sec-4',
      title: 'Scoreboard with Live Data',
      steps: [
        {
          id: '4a',
          type: 'action',
          title: 'Add the Football Scoreboard',
          instruction: 'Drag "Football Scoreboard" from the Football folder into the Project panel.',
          highlightSelector: '#panel-library',
          completionTrigger: {
            eventName: 'project:itemAdded',
            matchField: 'item',
            matchValue: 'contains:Football Scoreboard',
          },
          allowedSelectors: ['#panel-library', '#panel-project'],
          // audioUrl: 'TODO: Lesson_E5-10.mp3',
        },
        {
          id: '4b',
          type: 'action',
          title: 'Open the Data Controller',
          instruction: 'Select the scoreboard in your Project panel, then in Properties click the Data Controller dropdown and choose Sports > Scorebird > Football > New Input.',
          highlightSelector: '#panel-properties',
          completionTrigger: {
            eventName: 'data:newInputRequested',
            matchField: 'sport',
            matchValue: 'football',
          },
          allowedSelectors: ['#panel-properties', '#panel-project', '.dc-menu', '.dc-submenu'],
          // audioUrl: 'TODO: Lesson_E5-11.mp3',
        },
        {
          id: '4c',
          type: 'action',
          title: 'Connect to NeST',
          instruction: 'In the ScoreBird Settings tab, enter your NeST ID and click Connect — or leave the demo unit that is already filled in. Live data will start flowing into the scoreboard fields.',
          highlightSelector: '#panel-properties',
          completionTrigger: {
            eventName: 'data:sourceCreated',
          },
          allowedSelectors: ['#panel-properties'],
          // audioUrl: 'TODO: Lesson_E5-12.mp3',
        },
        {
          id: '4d',
          type: 'action',
          title: 'Take the Scoreboard Live',
          instruction: 'Click the Play button on the scoreboard row to send it to the Program Monitor. Your live camera feed and a data-driven scoreboard are now on air together — a complete broadcast look.',
          highlightSelector: '#panel-project',
          completionTrigger: {
            eventName: 'monitor:takeToProgram',
            matchField: 'itemName',
            matchValue: 'contains:Football Scoreboard',
          },
          allowedSelectors: ['#panel-project', '.project-play-btn'],
          // audioUrl: 'TODO: Lesson_E5-13.mp3',
        },
      ],
    },
    // Section 5: Air the Show (Video Board finale)
    {
      id: 'sec-5',
      title: 'Air the Show',
      steps: [
        {
          id: '5a',
          type: 'action',
          title: 'Open the Video Board',
          instruction: 'Click the "DISPLAY" button on the Program Monitor to open the Video Board. This is what your audience sees — a clean output of everything you just produced.',
          highlightSelector: '.monitor-combo',
          completionTrigger: {
            eventName: 'ui:videoBoardOpened',
          },
          allowedSelectors: ['#panel-program', '.monitor-combo', '.monitor-dropdown'],
          delayMs: 8000,
          // audioUrl: 'TODO: Lesson_E5-14.mp3',
        },
      ],
    },
  ],
};
